import { forwardRef, useState, type FocusEvent } from "react";
import { cn } from "@/lib/cn";
import { Input, type InputProps } from "./Input";

export interface ImporteInputProps extends Omit<InputProps, "onChange" | "value" | "type" | "leftSection" | "rightSection" | "inputMode"> {
  value: number | "";
  onChange: (value: number | "") => void;
  /** Código ISO de la moneda (MXN, USD…) · se muestra DENTRO del input, antes del importe. */
  moneda?: string | null;
}

const FORMATO = new Intl.NumberFormat("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Texto tecleado → número (acepta separadores de miles y el signo de moneda). */
function parsear(texto: string): number | "" {
  const limpio = texto.replace(/[^0-9.]/g, "");
  if (limpio === "" || limpio === ".") return "";
  const n = Number(limpio);
  return Number.isFinite(n) ? n : "";
}

/**
 * Input de IMPORTE · con el foco muestra el número crudo para editarlo cómodo; sin foco lo
 * muestra formateado (miles + 2 decimales, «22,500.00») con la moneda al inicio del campo. Al salir
 * redondea a 2 decimales. Reemplaza al `NumberInput` sin formato en los importes.
 */
export const ImporteInput = forwardRef<HTMLInputElement, ImporteInputProps>(
  ({ value, onChange, moneda, onFocus, onBlur, className, wrapperClassName, ...props }, ref) => {
    const [editando, setEditando] = useState<string | null>(null);

    const mostrado = editando ?? (value === "" ? "" : FORMATO.format(value));

    return (
      <Input
        ref={ref}
        type="text"
        inputMode="decimal"
        value={mostrado}
        onFocus={(e: FocusEvent<HTMLInputElement>) => {
          setEditando(value === "" ? "" : String(value));
          onFocus?.(e);
        }}
        onChange={(e) => {
          const texto = e.currentTarget.value;
          setEditando(texto);
          onChange(parsear(texto));
        }}
        onBlur={(e: FocusEvent<HTMLInputElement>) => {
          setEditando(null);
          if (value !== "") {
            const redondeado = Math.round(value * 100) / 100;
            if (redondeado !== value) onChange(redondeado);
          }
          onBlur?.(e);
        }}
        leftSection={moneda ? <span className="text-[10px] font-semibold">{moneda}</span> : undefined}
        // El contenedor se ajusta al ancho del input (`w-fit`) para que la moneda quede
        // DENTRO del campo y no en el borde de una celda más ancha.
        wrapperClassName={cn("w-fit", wrapperClassName)}
        // Con moneda, el número va justo después de ella («MXN 22,500.00») · sin moneda, a la derecha.
        className={cn("tabular-nums", moneda ? "pl-10 text-left" : "text-right", className)}
        {...props}
      />
    );
  }
);
ImporteInput.displayName = "ImporteInput";
